import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { FC, FunctionComponent, PropsWithChildren } from 'react'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { ElevationLevel, Transitions } from '~/assets/style/tokens'
import { ScrollbarStyle } from '~/assets/style/utils'
import { BetaBadge } from '~/components/BetaBadge'
import { Icon } from '~/components/Icon'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { RouterUnstyledLink } from '~/components/RouterUnstyledLink'
import { WithClassName } from '~/types/utils'
import { GroupItem, Section, SimpleItem } from './menu.config'

const MenuItemNameLabel = styled.span<{ hasNotifications?: boolean }>`
  opacity: 1;
  white-space: nowrap;
  transition: 0.15s ease-in-out;
  position: relative;

  ${({ hasNotifications }) =>
    hasNotifications &&
    css`
      :before {
        content: '';
        --menu-item-notification-size: 6px;
        background: ${Colors.Alert};
        min-width: var(--menu-item-notification-size);
        width: var(--menu-item-notification-size);
        max-width: var(--menu-item-notification-size);
        height: var(--menu-item-notification-size);
        border-radius: var(--menu-item-notification-size);
        position: absolute;
        top: 4px;
        right: -8px;
        ${ElevationLevel.low}
      }
    `}
`

const MenuItemName = ({ name, hasNotifications, releaseStage, staffOnly }: { name: string; hasNotifications?: boolean; releaseStage?: 'beta'; staffOnly?: boolean }) => (
  <MenuItemNameLabel hasNotifications={hasNotifications}>
    <span>{name}</span>
    {staffOnly && (
      <span title={'Pubstack staff only'} className={'ml-1.5 inline-flex align-middle'}>
        <Icon name={'admin'} width={'14px'} fill={Colors.SlateGrey} />
      </span>
    )}
    {releaseStage === 'beta' && <BetaBadge />}{' '}
  </MenuItemNameLabel>
)

const MenuItemWrapper = styled.div<{ active?: boolean; level: number; isOpen?: boolean }>`
  ${Fonts.colors.Hurricane};
  ${Fonts.P2};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  height: 22px;
  cursor: pointer;
  position: relative;
  transition: ease-in-out 300ms;
  border-radius: 2px;
  padding: 4px 4px 4px var(--mentu-item-wrapper-padding-left);

  ${({ level }) =>
    level === 1
      ? css`
          font-weight: 500;
          --mentu-item-wrapper-padding-left: 14px;
        `
      : css`
          --mentu-item-wrapper-padding-left: 20px;
        `}
  &:hover {
    text-decoration: none;
    background: ${Colors.Cloud};
    padding-left: calc(var(--mentu-item-wrapper-padding-left) + 4px);
  }

  ${({ active }) =>
    active &&
    css`
      color: ${Colors.King};
      background: ${Colors.Topaze};

      &:hover {
        background: ${Colors.Cloud};
      }
    `}

  & ${Icon} {
    transition: transform ${Transitions.default};
    transform: rotate(${({ isOpen }) => (isOpen ? '-180deg' : '0')});
  }
`
type MenuItemProps = Omit<PureMenuItem, 'to' | 'onClick'> & {
  active: boolean
  level: number
  isOpen?: boolean
} & (
    | {
        isGroup?: false
        isLink?: false
        to: string
      }
    | {
        isGroup: true
        isLink?: false
        onClick: () => void
      }
    | {
        isGroup?: false
        isLink: true
        onClick: () => void
      }
  )
const MenuItem: FunctionComponent<MenuItemProps> = ({ name, active, level, notifications, isOpen, releaseStage, staffOnly, ...props }) => {
  return props.isLink ? (
    <MenuItemWrapper onClick={props.onClick} active={active} level={level} isOpen={isOpen} data-tracking={`common-${normalizeAsTrackingLabel(name)}-navigate`}>
      <MenuItemName releaseStage={releaseStage} staffOnly={staffOnly} hasNotifications={!!notifications} name={name}></MenuItemName>
    </MenuItemWrapper>
  ) : props.isGroup ? (
    <MenuItemWrapper onClick={props.onClick} active={active} level={level} isOpen={isOpen}>
      <MenuItemName releaseStage={releaseStage} staffOnly={staffOnly} hasNotifications={!!notifications} name={name}></MenuItemName>
      <Icon name={'chevron_down'} width={'14px'} />
    </MenuItemWrapper>
  ) : (
    <RouterUnstyledLink to={props.to} data-tracking={`common-${normalizeAsTrackingLabel(name)}-navigate`}>
      <MenuItemWrapper active={active} level={level} isOpen={isOpen}>
        <MenuItemName releaseStage={releaseStage} staffOnly={staffOnly} hasNotifications={!!notifications} name={name}></MenuItemName>
      </MenuItemWrapper>
    </RouterUnstyledLink>
  )
}

type MenuGroupProps = PureMenuGroup & {
  onClick: () => void
  open?: boolean
}
const MenuGroup: FunctionComponent<MenuGroupProps> = ({ onClick, name, open, items }) => {
  return (
    <>
      <MenuItem onClick={onClick} active={false} level={1} name={name} isGroup isOpen={open} />
      {!!open && items.map((item, i) => <MenuItem {...item} key={i} level={2} />)}
    </>
  )
}

const MenuSectionTitle = styled.div`
  ${Fonts.colors.SlateGrey};
  ${Fonts.P3};
  line-height: 24px;
  padding-left: 8px;
  font-weight: 500;
  opacity: 1;
  width: auto;
  transition: 0.15s ease-in-out;
  display: flex;
  flex-direction: row;
  gap: 2px;
  align-items: center;
  ${Icon} {
    flex-grow: 0;
  }
`

const MenuSectionWrapper = styled.div`
  margin-top: 18px;
  &:first-of-type {
    margin-top: 0;
  }

  transition: 0.15s ease-in-out;

  display: flex;
  flex-direction: column;
  gap: 4px;
`

type MenuSectionProps = PureMenuSection
const MenuSection: FC<PropsWithChildren<MenuSectionProps>> = ({ title, items, icon }) => {
  return (
    <MenuSectionWrapper>
      {title && (
        <MenuSectionTitle>
          {!!icon && <Icon name={icon} width={'15px'} />}
          {title}
        </MenuSectionTitle>
      )}
      {items.map((item, i) => (item.type === 'group' ? <MenuGroup {...item} key={i} /> : <MenuItem {...item} key={i} level={1} />))}
    </MenuSectionWrapper>
  )
}

const MenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1 1 auto;
  height: 100%;
  padding: 0 8px;

  > div:first-of-type {
    overflow: auto;
    ${ScrollbarStyle};
    flex: 1 1 0;
  }
`

export type PureMenuItem = SimpleItem & { active: boolean }
export type PureMenuGroup = Omit<GroupItem, 'items'> & { items: PureMenuItem[] } & { onClick: () => void }
export type PureMenuSection = Omit<Section, 'items'> & { items: (PureMenuItem | PureMenuGroup)[] }
export type PureMenuProps = WithClassName & {
  sections: PureMenuSection[]
  bottomItems: PureMenuItem[]
}
const UnstyledPureMenu: FunctionComponent<PureMenuProps> = ({ sections, bottomItems, className }) => {
  return (
    <MenuWrapper className={className}>
      <div>
        {sections.map((section, index) => (
          <MenuSection {...section} key={index} />
        ))}
      </div>
      {/* `shrink-0`, or the footer collapses under its own content and the scrolling nav above runs
          underneath it — visible on a full nav at a short viewport, where Floor price and Settings
          close to 21px apart against a 32px rhythm and the descenders clip. */}
      <div className={'shrink-0'}>
        {bottomItems.map((item, index) => (
          <MenuItem {...item} key={index} level={1} />
        ))}
      </div>
    </MenuWrapper>
  )
}
export const PureMenu = styled(UnstyledPureMenu)``
